import React from 'react';
import PropTypes from 'prop-types';

function renderMessageWithMailto(message) {
  if (!message || typeof message !== 'string') return message;

  const parts = [];
  const regex = /mailto:([^\s]+)/gi;
  let lastIndex = 0;
  let match = regex.exec(message);

  while (match) {
    if (match.index > lastIndex) {
      let text = message.slice(lastIndex, match.index);
      text = text.replace(/\s+at\s*$/i, ' ');
      parts.push({ type: 'text', value: text });
    }
    parts.push({ type: 'mailto', value: match[1] });
    lastIndex = regex.lastIndex;
    match = regex.exec(message);
  }

  if (lastIndex < message.length) {
    parts.push({ type: 'text', value: message.slice(lastIndex) });
  }

  if (parts.length === 0) return message;

  return parts.map((part) => {
    if (part.type === 'mailto') {
      return (
        <a key={`mailto-${part.value}`} href={`mailto:${part.value}`}>
          {part.value}
        </a>
      );
    }
    return <React.Fragment key={`text-${part.value.slice(0, 24)}-${part.value.length}`}>{part.value}</React.Fragment>;
  });
}

function extractMessageFromDetail(detail) {
  if (detail == null) return null;
  if (typeof detail !== 'string') return String(detail);
  let trimmed = detail.trim();
  if (!trimmed) return null;

  for (let i = 0; i < 2; i += 1) {
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object' && typeof parsed.message === 'string' && parsed.message.trim()) {
          return parsed.message.trim();
        }
        return trimmed;
      } catch (_) {
        return trimmed;
      }
    }

    if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
      try {
        trimmed = JSON.parse(trimmed);
      } catch (_) {
        trimmed = trimmed.slice(1, -1);
      }
      if (typeof trimmed !== 'string') return String(trimmed);
      trimmed = trimmed.trim();
      if (!trimmed) return null;
    } else {
      break;
    }
  }

  return trimmed;
}

function resolveErrorContent(error, titleOverride) {
  if (typeof error === 'string') {
    return {
      title: titleOverride || 'Something went wrong',
      message: error,
      traceId: null,
    };
  }

  const errorData = error.response?.data || error;
  const detail = errorData.detail || error.detail;
  const traceId = errorData.traceId || error.traceId;
  const statusRaw = error.response?.status || error.status || error.statusCode;
  const status = statusRaw != null ? Number(statusRaw) : null;

  let message = 'Something went wrong. Please try again.';

  if (status != null && status >= 400 && status < 500) {
    if (typeof error.response?.data === 'string' && error.response.data.trim() && error.response.data.trim() !== 'Bad Request') {
      message = error.response.data.trim();
      if ((message.startsWith('"') && message.endsWith('"')) || (message.startsWith("'") && message.endsWith("'"))) {
        message = message.slice(1, -1);
      }
    } else if (typeof error.response?.data === 'object' && error.response.data !== null) {
      if (error.response.data.message && typeof error.response.data.message === 'string') {
        message = error.response.data.message.trim();
      } else if (error.response.data.error && typeof error.response.data.error === 'string') {
        message = error.response.data.error.trim();
      } else if (error.response.data.detail) {
        message = extractMessageFromDetail(error.response.data.detail) || message;
      }
    } else if (detail) {
      message = extractMessageFromDetail(detail) || message;
    } else if (error.message) {
      const lowerMessage = error.message.toLowerCase();
      if (!lowerMessage.includes('bad request') && !lowerMessage.includes('could not update') && !lowerMessage.match(/^\d{3}/)) {
        message = error.message.trim();
      }
    }
  } else if (detail) {
    message = extractMessageFromDetail(detail) || message;
  } else if (error.message) {
    message = error.message;
  }

  if (message === 'Something went wrong. Please try again.' && detail) {
    message = extractMessageFromDetail(detail) || message;
  }

  const friendlyTitle =
    titleOverride ||
    (typeof errorData.title === 'string' &&
    errorData.title.trim() &&
    !/exception|error$/i.test(errorData.title.trim())
      ? errorData.title.trim()
      : 'Something went wrong');

  return { title: friendlyTitle, message, traceId };
}

function ErrorMessage({ error, onDismiss, className, title, description }) {
  if (!error && !description) return null;

  const { title: resolvedTitle, message, traceId } = error
    ? resolveErrorContent(error, title)
    : { title: title || 'Something went wrong', message: null, traceId: null };
  const body = description ?? message;

  return (
    <div
      className={`flex items-start gap-3 rounded-[12px] border-[1.5px] border-[#F0D0C8] bg-[#FDF4F2] px-4 py-3.5 ${className || ''}`}
      role='alert'
    >
      <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#C0392B]'>
        <svg width='17' height='17' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
          <circle cx='12' cy='12' r='10' />
          <line x1='12' y1='8' x2='12' y2='12' />
          <line x1='12' y1='16.01' x2='12.01' y2='16.01' />
        </svg>
      </div>
      <div className='min-w-0 flex-1 pt-0.5'>
        <div className='text-sm font-semibold leading-5 text-[#0B1929]'>{resolvedTitle}</div>
        {body ? (
          <div className='mt-0.5 text-[13px] leading-5 text-[#5C3A36] [&_a]:font-medium [&_a]:text-[#2E6BAD] [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-[#1E4E80]'>
            {typeof body === 'string' ? renderMessageWithMailto(body) : body}
            {traceId ? (
              <span className='mt-1.5 block text-[11px] text-[#8A6A66]'>
                Trace ID: {traceId}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      {onDismiss ? (
        <button
          type='button'
          onClick={onDismiss}
          className='-mr-1 -mt-0.5 inline-flex shrink-0 rounded-md p-1.5 text-[#8A6A66] transition hover:bg-[#F5E4E0] hover:text-[#0B1929] focus:outline-none focus:ring-2 focus:ring-[#C0392B]/30'
          aria-label='Dismiss'
        >
          <svg className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor' aria-hidden='true'>
            <path d='M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z' />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

ErrorMessage.propTypes = {
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Error),
    PropTypes.shape({
      status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      statusCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      title: PropTypes.string,
      detail: PropTypes.string,
      message: PropTypes.string,
      instance: PropTypes.string,
      requestId: PropTypes.string,
      traceId: PropTypes.string,
      response: PropTypes.shape({
        status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        statusText: PropTypes.string,
        data: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      }),
    }),
  ]),
  onDismiss: PropTypes.func,
  className: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.node,
};

export default ErrorMessage;
